import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import { useChangeTier } from '~/hooks/apis/loyalty/useChangeTier';

import type { TierChangeRequestBody } from '~/types/loyaltyContract';

type TargetTier = TierChangeRequestBody['targetTier'];

const TIER_OPTIONS: Array<TargetTier> = ['silver', 'gold', 'platinum'];
const TIER_CHANGE_REMARK_SOURCE = 'ADMIN_PORTAL_MANUAL_UPGRADE';

interface Props {
  open: boolean;
  onClose: () => void;
  customerId: string;
  currentTier: App.LuxLoyaltyTier;
  onSuccess: () => void;
}

export default function LuxLoyaltyChangeTierModal({ open, onClose, customerId, currentTier, onSuccess }: Props) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const tierRank: Record<App.LuxLoyaltyTier, number> = { bronze: 0, silver: 1, gold: 2, platinum: 3 };
  const availableTiers = TIER_OPTIONS.filter((tier) => tierRank[tier] > tierRank[currentTier]);

  const [targetTier, setTargetTier] = useState<TargetTier>(availableTiers[0] ?? 'silver');
  const [reason, setReason] = useState('');

  const changeTierMutation = useChangeTier({
    onSuccess: () => {
      enqueueSnackbar(`Tier upgraded to ${targetTier} successfully`, { variant: 'success' });
      setReason('');
      onSuccess();
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to upgrade tier: ${error.message}`, { variant: 'error' });
    },
  });

  const canSubmit = targetTier && reason.trim() && !changeTierMutation.isPending;

  const handleSubmit = () => {
    changeTierMutation.mutate({
      customerId,
      targetTier,
      reason: reason.trim(),
      remarkSource: TIER_CHANGE_REMARK_SOURCE,
    });
  };

  const tierPalette = theme.palette.loyaltyTiers[targetTier];
  const accentColor = tierPalette?.main ?? theme.palette.primary.main;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: 3,
          borderTopColor: accentColor,
          transition: 'border-color 0.3s ease',
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Change Tier
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manually upgrade this customer's Société tier. This action takes effect immediately. Tier downgrades are not
          supported.
        </Typography>

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Target Tier *
            </Typography>
            {availableTiers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                This customer is already at the highest tier (Platinum).
              </Typography>
            ) : (
              <Stack direction="row" spacing={1.5}>
                {availableTiers.map((tier) => {
                  const palette = theme.palette.loyaltyTiers[tier];
                  const color = palette?.main ?? theme.palette.grey[500];
                  const isSelected = targetTier === tier;
                  return (
                    <ButtonBase
                      key={tier}
                      onClick={() => setTargetTier(tier)}
                      sx={{
                        flex: 1,
                        py: 1.5,
                        px: 2,
                        borderRadius: 2,
                        border: 2,
                        borderColor: isSelected ? color : 'divider',
                        bgcolor: isSelected ? alpha(color, 0.08) : 'transparent',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        '&:hover': {
                          borderColor: color,
                          bgcolor: alpha(color, 0.04),
                        },
                      }}
                    >
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
                      <Typography variant="subtitle2" fontWeight={isSelected ? 700 : 500}>
                        {tier.charAt(0).toUpperCase() + tier.slice(1)}
                      </Typography>
                      {isSelected && <CheckCircleOutlineIcon sx={{ fontSize: 16, color }} />}
                    </ButtonBase>
                  );
                })}
              </Stack>
            )}
          </Box>

          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            placeholder="Provide a reason for this tier change..."
          />

          <Divider />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="text" onClick={onClose} disabled={changeTierMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!canSubmit || availableTiers.length === 0}
              endIcon={
                changeTierMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />
              }
              sx={{ px: 4 }}
            >
              {changeTierMutation.isPending ? 'Submitting...' : 'Change Tier'}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
